import type { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import type { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import Individual from "marketplace/individuals/individual";
import {
  credentialsMissingReason,
  isKnownToLackCredentials,
} from "marketplace/individuals/profiles/professional-credentials";
import type { UserCoworkerJSON } from "users/information/coworker-information/_schemas/user-coworker-schema";
import type { UserInformationJSON } from "users/information/_schemas/user-information-schema";
import type { User } from "users/user";

/**
 * One person the team people picker can offer, whichever source found them (#6602).
 *
 * The sources answer in five shapes — a coworker, a network connection, a marketplace individual, a
 * search hit and a marketplace team's nested member — and the picker only ever needs the same five
 * facts from each. A team member is a user id and a legal entity id; the rest is what the row says.
 * Kept as plain values, so the selection can hold people from every source side by side and a
 * person found twice is still one person.
 */
export type TeamPickerPerson = {
  userId: string;
  legalEntityId: string;
  name: string;
  /** What the row says under the name: the person's firm, when the source knows it. */
  detail?: string;
  /**
   * Why a vendor team would refuse this person as a member, when the source can tell. Only the
   * credentials rule (#5800) is known this early; everything else the API still decides on save.
   */
  credentialsRefusal?: string;
};

/**
 * Whether the person works for this legal entity. A missing entity names nobody. Compared without
 * case, because the sources do not agree on how a Guid is cased.
 */
export function worksFor(person: TeamPickerPerson, legalEntityId: string | undefined): boolean {
  return Boolean(legalEntityId) && person.legalEntityId.toLowerCase() === legalEntityId?.toLowerCase();
}

function fullName(firstName: string | null | undefined, lastName: string | null | undefined): string {
  return [firstName, lastName].filter(Boolean).join(" ").trim();
}

/**
 * Whether the marketplace record shows this person's credentials missing. A record the picker cannot
 * read as an individual, or no record at all, says nothing either way, so it refuses nobody.
 */
function lacksCredentials(json: IndividualJSON | undefined): boolean {
  try {
    return isKnownToLackCredentials(Individual.fromJSON(json) ?? undefined);
  } catch {
    return false;
  }
}

export function personFromCoworker(coworker: UserCoworkerJSON): TeamPickerPerson {
  return {
    userId: coworker.userId,
    legalEntityId: coworker.legalEntityId,
    name: fullName(coworker.firstName, coworker.lastName) || coworker.email,
    detail: coworker.entityName,
    credentialsRefusal: coworker.hasCredentials === false ? credentialsMissingReason : undefined,
  };
}

/** What the row under the signed-in user's own name says, so nobody mistakes themselves for a namesake. */
export const SELF_DETAIL = "You";

/**
 * The signed-in user, with their own marketplace record when the picker has it. Coworkers lists
 * everyone at the firm but the caller, and a sole practitioner has nobody else to choose as leader.
 * A session that has not loaded its user yet offers nobody.
 */
export function personFromSessionUser(user: User | null | undefined, individual?: IndividualJSON): TeamPickerPerson | undefined {
  if (!user?.id) return undefined;
  return {
    userId: user.id.value,
    legalEntityId: user.legalEntityId.value,
    name: user.fullName,
    detail: SELF_DETAIL,
    credentialsRefusal: lacksCredentials(individual) ? credentialsMissingReason : undefined,
  };
}

/**
 * A network connection, with their marketplace record when the picker has it. The connection alone
 * does not carry credentials, so the record is what lets the row decline somebody a team would refuse.
 */
export function personFromNetworkConnection(
  connection: UserInformationJSON,
  individual?: IndividualJSON,
): TeamPickerPerson {
  return {
    userId: connection.id,
    legalEntityId: connection.legalEntityId,
    name: fullName(connection.firstName, connection.lastName),
    detail: connection.companyName ?? undefined,
    credentialsRefusal: lacksCredentials(individual) ? credentialsMissingReason : undefined,
  };
}

export function personFromIndividual(individual: IndividualJSON): TeamPickerPerson {
  return {
    userId: individual.userId,
    legalEntityId: individual.legalEntityId,
    name: fullName(individual.firstName, individual.lastName),
    detail: individual.companyName ?? undefined,
    credentialsRefusal: lacksCredentials(individual) ? credentialsMissingReason : undefined,
  };
}

/**
 * A person in a marketplace search answer: an individual hit, or a member nested in a team hit. A
 * hit without the two ids a team member is made of cannot be added, so it is not offered.
 */
export function personFromSearchHit(
  hit: Pick<MarketplaceSearchInfoJSON, "userId" | "legalEntityId" | "firstName" | "lastName" | "companyName">,
): TeamPickerPerson | undefined {
  if (!hit.userId || !hit.legalEntityId) return undefined;
  return {
    userId: hit.userId,
    legalEntityId: hit.legalEntityId,
    name: fullName(hit.firstName, hit.lastName),
    detail: hit.companyName ?? undefined,
  };
}
